import React, {createContext, useContext, useCallback, useMemo, useEffect} from "react";
import useSWR from "swr";
import axiosInstance from "../api/axios";
import {getFetcher} from "../api/fetchers";
import {UserAccount} from "../../schemas/user/userAccountSchemas";
import UserRole, {roleHierarchy} from "../../schemas/user/roles";

interface AuthContextType {
    user: UserAccount | undefined;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    hasPermission: (requiredRole: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const storedUser = JSON.parse(localStorage.getItem("user") ?? "null") as UserAccount | undefined;
    const {
        data: user,
        error,
        mutate,
        isLoading: isLoadingSWR
    } = useSWR<UserAccount>("/users/me", getFetcher<UserAccount>);

    const isAuthenticated = !!user && !error;
    const isLoading = isLoadingSWR && !error && user !== undefined;

    const userData = user || storedUser;
    const isAuthenticatedOrStored = isAuthenticated || !!storedUser;

    useEffect(() => {
        if (user) {
            localStorage.setItem("user", JSON.stringify(user));
        } else {
            localStorage.removeItem("user");
        }
    }, [user]);

    const login = useCallback(async (username: string, password: string) => {
        const response = await axiosInstance.post("/auth/login", {username, password});
        if (response?.status === 200) {
            await mutate();
        } else {
            throw new Error("Login failed");
        }
    }, [mutate]);

    const logout = useCallback(async () => {
        await axiosInstance.post("/auth/logout");
        localStorage.removeItem("user");
        await mutate(undefined, {revalidate: false});
    }, [mutate]);

    const hasPermission = useCallback((requiredRole: UserRole): boolean => {
        if (!userData) return false;
        const userRoles = userData.roles;
        if (!userRoles) {
            return false;
        }
        return userRoles.some((role) => roleHierarchy[role].includes(requiredRole));
    }, [userData]);

    const authContextValue = useMemo(() => ({
        user: userData,
        isAuthenticated: isAuthenticatedOrStored,
        isLoading,
        login,
        logout,
        hasPermission,
    }), [userData, isAuthenticatedOrStored, isLoading, login, logout, hasPermission]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};
